import { useEffect } from 'react';
import { storeFirebaseUser, UserKeys } from '../../api/users-api';
import { User } from 'firebase/auth';
import { auth, database } from './firebase-config';
import { onDisconnect, ref, remove } from 'firebase/database';
import { useStore } from '../../components/state/zustand-store';
import { useUserSettings } from '../../utils/use-user-settings';
import { useQueryClient } from '@tanstack/react-query';
import { UserSettings } from '../../../types';

const useFirebaseChannelSetup = () => {
  const {
    firebaseDbPath: firebaseDbPathFromUserSettings,
    primaryDomain,
    loggedInUser,
  } = useUserSettings();
  const { setFirebaseUser, firebaseUser } = useStore();
  const queryClient = useQueryClient();

  async function createFirebaseUser(user: User) {
    // replace @ and . with - and _ for firebase path
    const cleanDomain = primaryDomain.replace(/@/g, '-').replace(/\./g, '_');

    // create new datetime "channelId"
    const firebaseDbPath = `${cleanDomain}/${user.uid}/${Date.now()}`;

    const firebaseUser = {
      firebaseDbPath,
      firebaseUserId: user.uid,
      userEmail: loggedInUser,
      primaryDomain,
      alive: parseInt(firebaseDbPath.split('/').pop()!),
      lastKeepAliveMessageSent: parseInt(firebaseDbPath.split('/').pop()!),
    };

    // update in datastore
    const fbUser = await storeFirebaseUser(firebaseUser);
    setFirebaseUser(fbUser);

    queryClient.setQueryData<UserSettings>([UserKeys.UserSettings], (old) => {
      if (!old) return old;

      return {
        ...old,
        firebaseDbPath: firebaseUser.firebaseDbPath,
      };
    });

    // delete realtime db data on disconnect
    const dbRef = ref(database, firebaseDbPath);
    onDisconnect(dbRef).remove();
  }

  useEffect(() => {
    if (!auth.currentUser) {
      console.log('No user found');
    } else {
      if (firebaseDbPathFromUserSettings === '') {
        createFirebaseUser(auth.currentUser);
        return;
      }

      if (firebaseDbPathFromUserSettings !== firebaseUser.firebaseDbPath) {
        setFirebaseUser({
          firebaseDbPath: firebaseDbPathFromUserSettings,
          firebaseUserId: auth.currentUser.uid,
          userEmail: loggedInUser,
          primaryDomain,
          alive: Date.now(),
          lastKeepAliveMessageSent: Date.now(),
        });
        return;
      }
    }

    return () => {
      if (firebaseUser.firebaseDbPath === '') return;
      const dbRef = ref(database, firebaseUser.firebaseDbPath);
      remove(dbRef);
    };
  }, [
    firebaseDbPathFromUserSettings,
    firebaseUser.firebaseDbPath,
    setFirebaseUser,
    primaryDomain,
    loggedInUser,
  ]);
};

export default useFirebaseChannelSetup;
